import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
  createFilterOptions,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import useColors from "../../api/hooks/useColors";
import { installedProgramKeys } from "../../api/queryKeys";
import SvgToIcon from "../../icons/SvgToIcon";
import {
  isRawDataField,
  parseRawDataField,
} from "../../scenes/stack/environment/entitiesTable/fieldUtils";
import { ALL_ENTITIES } from "../../scenes/stack/environment/entityOverviewPage/TableView/EntitiesOverviewTableView";
import { icons } from "../../scenes/stack/environment/icons/iconExport";
import IntegrationImage from "../../scenes/stack/integrations/components/IntegrationImage";
import { IntegrationsMapping } from "../../scenes/stack/integrations/IntegrationsMapping";
import TextButton from "../buttons/TextButton";
import { StyledTextField } from "../forms/styles/formFieldStyles";
import LoadingIcon from "../LoadingIcon";

const OPERATORS = [
  { value: "=", label: "Equals" },
  { value: "!=", label: "Not Equals" },
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Not Contains" },
  { value: "exists", label: "Exists" },
  { value: "not_exists", label: "Not Exists" },
  { value: ">", label: "Greater Than" },
  { value: "<", label: "Less Than" },
  { value: ">=", label: "Greater or Equal" },
  { value: "<=", label: "Less or Equal" },
];

const PROGRAM_OPERATORS = [
  { value: "contains", label: "Contains" },
  { value: "not_contains", label: "Not Contains" },
];

const PROGRAM_DATA_TYPE = "program";

export const FILTER_TARGET_TABLE = {
  ENTITIES: "entities",
  PROGRAMS: "programs",
};

const formatFieldName = (fieldName) => {
  if (isRawDataField(fieldName)) {
    const { integration, field } = parseRawDataField(fieldName);
    const formattedField = field
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    const formattedIntegration = integration
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
    return `${formattedField} (${formattedIntegration}) `;
  }
  return fieldName
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getFieldKey = (field) => {
  return `${field.field_name}${
    field.source_integration ? `_${field.source_integration}` : ""
  }`;
};

// Filters out duplicate fields by name for all Entities view
const filterDuplicateFieldsByName = (fields) => {
  const uniqueFields = new Set();
  const filteredFields = fields.filter((field) => {
    const key = getFieldKey(field);
    if (uniqueFields.has(key)) {
      return false;
    }
    uniqueFields.add(key);
    return true;
  });
  return filteredFields;
};

const applyDefaultTargetTable = (filterList) =>
  filterList.map((filter) => {
    const formattedName =
      filter.formattedName ||
      (filter.field ? formatFieldName(filter.field) : undefined);
    return {
      ...filter,
      formattedName,
      target_table: filter.target_table ?? FILTER_TARGET_TABLE.ENTITIES,
    };
  });

const EMPTY_FILTER = {
  field: "",
  op: "=",
  value: "",
  target_table: FILTER_TARGET_TABLE.ENTITIES,
};

const createEmptyFilter = () => ({ ...EMPTY_FILTER });

export default function EntityFilter({
  type,
  onSearch,
  entityType,
  initialFilters = null,
  initialFilterLogic = null,
}) {
  const { getEntityFields, fetchProgramFilterOptions } = useAPI();
  const [filters, setFilters] = useState(
    initialFilters && initialFilters.length > 0
      ? applyDefaultTargetTable(initialFilters)
      : applyDefaultTargetTable([createEmptyFilter()])
  );
  const [filterLogic, setFilterLogic] = useState(
    initialFilterLogic && initialFilterLogic.length > 0
      ? initialFilterLogic
      : []
  );
  const colors = useColors();

  useEffect(() => {
    setFilters(
      initialFilters && initialFilters.length > 0
        ? applyDefaultTargetTable(initialFilters)
        : applyDefaultTargetTable([createEmptyFilter()])
    );
    setFilterLogic(
      initialFilterLogic && initialFilterLogic.length > 0
        ? initialFilterLogic
        : []
    );
  }, [initialFilters, initialFilterLogic]);

  const { data: fieldsData, isLoading: isLoadingFields } = useQuery({
    queryKey: ["entityFields", entityType],
    queryFn: () => getEntityFields(entityType),
    select: (response) =>
      type === ALL_ENTITIES
        ? filterDuplicateFieldsByName(response.data)
        : response.data,
  });

  const { data: programFilterOptionsData, isLoading: isLoadingProgramOptions } =
    useQuery({
      queryKey: installedProgramKeys.filterOptions(),
      queryFn: fetchProgramFilterOptions,
      select: (response) =>
        response?.data || { program_names: [], program_versions: [] },
    });

  const programNameOptions = useMemo(
    () => programFilterOptionsData?.program_names || [],
    [programFilterOptionsData]
  );

  const programVersionsByName = useMemo(() => {
    const versions = programFilterOptionsData?.program_versions || [];
    return versions.reduce((acc, item) => {
      acc[item.name] = item.versions;
      return acc;
    }, {});
  }, [programFilterOptionsData]);

  const selectStyles = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
      borderColor: colors.divider2,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: "0.5px solid " + colors.divider2,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        border: "0.5px solid " + colors.grey_500,
      },
    },

    // Add styles for when an option is selected
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: colors.grey_500,
      borderWidth: "1px",
    },

    // Add styles for when dropdown is open
    "&.MuiOutlinedInput-root.Mui-focused": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.grey_500,
        borderWidth: "1px",
      },
    },

    "& .MuiInputBase-input::placeholder": {
      color: colors.text_primary,
    },
    width: "100%",
    "& .MuiInputBase-root": { height: "32px" },
  };
  const fieldOptions = useMemo(() => {
    const fields = fieldsData || [];
    const mappedFields = fields
      .filter((field) => field && field.field_name)
      .map((field) => ({
        ...field,
        formattedName: formatFieldName(field.field_name),
        label: formatFieldName(field.field_name),
        value: field.field_name,
        group: field.source_integration
          ? field.source_integration
              .split("_")
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ")
          : "General",
      }));

    const breezeCodeName = IntegrationsMapping.breeze.codeName;
    const installedProgramFieldValue = "installed_programs";
    const installedProgramFieldName = `${breezeCodeName}>${installedProgramFieldValue}`;
    const programFields = [
      {
        source_integration: breezeCodeName,
        field_name: installedProgramFieldName,
        formattedName: formatFieldName(installedProgramFieldName),
        label: formatFieldName(installedProgramFieldName),
        value: installedProgramFieldValue,
        group: IntegrationsMapping.breeze.shortDisplayName,
        data_type: PROGRAM_DATA_TYPE,
        target_table: FILTER_TARGET_TABLE.PROGRAMS,
      },
    ];

    return [...programFields, ...mappedFields].sort((a, b) => {
      if (a.group !== b.group) {
        return a.group.localeCompare(b.group);
      }
      return a.label.localeCompare(b.label);
    });
  }, [fieldsData]);

  const filterOptions = useMemo(
    () =>
      createFilterOptions({
        matchFrom: "any",
        stringify: (option) => {
          if (!option || typeof option !== "object") return "";
          const parts = [
            option.field_name,
            option.formattedName,
            option.label,
            option.group,
            option.source_integration,
          ]
            .filter(Boolean)
            .filter(
              (part) => typeof part === "string" && part.trim().length > 0
            );
          return parts.join(" ");
        },
        trim: true,
      }),
    []
  );

  const getFieldOperatorOptions = (field) => {
    return field?.data_type === PROGRAM_DATA_TYPE
      ? PROGRAM_OPERATORS
      : OPERATORS;
  };
  const getFilterOperatorOptions = (filter) => {
    return filter?.target_table === FILTER_TARGET_TABLE.PROGRAMS
      ? PROGRAM_OPERATORS
      : OPERATORS;
  };

  const handleFilterChange = (index, key, value) => {
    const newFilters = [...filters];
    newFilters[index][key] = value;

    if (key === "field") {
      const selectedField = fieldOptions.find(
        (option) => option.value === value
      );
      newFilters[index].formattedName =
        selectedField?.formattedName || (value ? formatFieldName(value) : "");
      newFilters[index].target_table =
        selectedField?.target_table || FILTER_TARGET_TABLE.ENTITIES;
      newFilters[index].value =
        selectedField?.data_type === PROGRAM_DATA_TYPE
          ? { name: "", version: "" }
          : "";
      const operatorOptions = getFieldOperatorOptions(selectedField);
      const currentOp = newFilters[index].op;
      const hasValidOperator = operatorOptions.some(
        (option) => option.value === currentOp
      );
      newFilters[index].op = hasValidOperator
        ? currentOp
        : operatorOptions[0].value;
    }
    setFilters(newFilters);
  };

  const addFilter = () => {
    setFilters([...filters, createEmptyFilter()]);
    if (filters.length > 0) {
      setFilterLogic([...filterLogic, "AND"]);
    }
  };

  const removeFilter = (index) => {
    const newFilters = filters.filter((_, i) => i !== index);
    setFilters(newFilters);

    if (newFilters.length === 0) {
      setFilterLogic([]);
    } else if (index === 0) {
      setFilterLogic(filterLogic.slice(1));
    } else if (index === filters.length - 1) {
      setFilterLogic(filterLogic.slice(0, -1));
    } else {
      const newLogic = [...filterLogic];
      newLogic.splice(index - 1, 1);
      setFilterLogic(newLogic);
    }
  };

  const handleLogicChange = (index, logic) => {
    const newLogic = [...filterLogic];
    newLogic[index] = logic;
    setFilterLogic(newLogic);
  };

  const parseMultiValue = (value) => {
    if (!value || typeof value !== "string") return [];
    return value
      .split(/[,\n]/)
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
  };

  const handleSearch = () => {
    const validFilters = filters.reduce((acc, filterItem) => {
      if (!filterItem.field) {
        return acc;
      }

      const targetTable =
        filterItem.target_table ?? FILTER_TARGET_TABLE.ENTITIES;
      const formattedName =
        filterItem.formattedName ||
        (filterItem.field ? formatFieldName(filterItem.field) : "");
      const dataType = getFieldDataType(filterItem.field);

      if (
        dataType === PROGRAM_DATA_TYPE &&
        filterItem.op !== "exists" &&
        filterItem.op !== "not_exists"
      ) {
        const programValue =
          filterItem.value && typeof filterItem.value === "object"
            ? filterItem.value
            : {};
        const programName = programValue.name || "";
        const programVersion = programValue.version || "";

        if (!programName) {
          return acc;
        }

        acc.push({
          field: filterItem.field,
          op: filterItem.op,
          value: { name: programName, version: programVersion },
          formattedName,
          target_table: targetTable,
        });
        return acc;
      }

      if (filterItem.op === "exists" || filterItem.op === "not_exists") {
        acc.push({
          field: filterItem.field,
          op: filterItem.op,
          value: "",
          formattedName,
          target_table: targetTable,
        });
        return acc;
      }

      if (filterItem.value === "") {
        return acc;
      }

      const isMultiValue =
        (filterItem.op === "contains" ||
          filterItem.op === "=" ||
          filterItem.op === "not_contains" ||
          filterItem.op === "!=") &&
        dataType === "string";

      if (isMultiValue) {
        const values = parseMultiValue(filterItem.value);
        acc.push({
          ...filterItem,
          formattedName,
          target_table: targetTable,
          value: values.length === 1 ? values[0] : values,
          isMultiValue: values.length > 1,
        });
        return acc;
      }

      acc.push({
        ...filterItem,
        formattedName,
        target_table: targetTable,
      });
      return acc;
    }, []);
    const validLogic = filterLogic.slice(0, validFilters.length - 1);
    onSearch(validFilters, validLogic);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const getFieldDataType = (fieldName) => {
    const fieldOption = fieldOptions.find((f) => f.value === fieldName);
    if (fieldOption?.data_type) {
      return fieldOption.data_type;
    }

    const fields = fieldsData || [];
    const field = fields.find((f) => f.field_name === fieldName);
    return field?.data_type || "string";
  };

  const renderValueInput = (filter, index) => {
    const dataType = getFieldDataType(filter.field);

    if (dataType === PROGRAM_DATA_TYPE) {
      const programValue =
        filter.value && typeof filter.value === "object" ? filter.value : {};
      const selectedName = programValue.name || "";
      const selectedVersion = programValue.version || "";
      const versionOptions = [
        "Any version",
        ...(programVersionsByName[selectedName] || []),
      ];

      return (
        <Box display="flex" flexDirection="column" gap={1}>
          <Autocomplete
            fullWidth
            size="small"
            options={programNameOptions}
            loading={isLoadingProgramOptions}
            value={selectedName || null}
            onChange={(_, newValue) =>
              handleFilterChange(index, "value", {
                name: newValue || "",
                version: "",
              })
            }
            isOptionEqualToValue={(option, value) => option === value}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                placeholder="Program name"
                sx={{
                  "& .MuiOutlinedInput-input": {
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "14px",
                    letterSpacing: "0.48px",
                  },
                }}
              />
            )}
          />
          <Autocomplete
            fullWidth
            size="small"
            options={versionOptions}
            loading={isLoadingProgramOptions}
            value={selectedVersion || "Any version"}
            onChange={(_, newValue) => {
              const normalizedVersion =
                !newValue || newValue === "Any version" ? "" : newValue;
              handleFilterChange(index, "value", {
                name: selectedName,
                version: normalizedVersion,
              });
            }}
            isOptionEqualToValue={(option, value) => option === value}
            disabled={!selectedName}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                placeholder="Program version"
                sx={{
                  "& .MuiOutlinedInput-input": {
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                    lineHeight: "14px",
                    letterSpacing: "0.48px",
                  },
                }}
              />
            )}
          />
        </Box>
      );
    }

    if (filter.op === "exists") {
      return (
        <StyledTextField
          fullWidth
          size="small"
          placeholder="N/A - searches if field exists"
          value=""
          disabled
          sx={{
            "& .MuiOutlinedInput-input": {
              fontSize: "14px",
              fontFamily: "Poppins, sans-serif",
              lineHeight: "14px",
              letterSpacing: "0.48px",
              color: colors.text_secondary,
            },
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.grey_8percent,
            },
          }}
        />
      );
    }

    if (filter.op === "not_exists") {
      return (
        <StyledTextField
          fullWidth
          size="small"
          placeholder="N/A - searches if field does not exist"
          value=""
          disabled
          sx={{
            "& .MuiOutlinedInput-input": {
              fontSize: "14px",
              fontFamily: "Poppins, sans-serif",
              lineHeight: "14px",
              letterSpacing: "0.48px",
              color: colors.text_secondary,
            },
            "& .MuiOutlinedInput-root": {
              backgroundColor: colors.grey_8percent,
            },
          }}
        />
      );
    }

    const isMultiValue =
      (filter.op === "contains" ||
        filter.op === "=" ||
        filter.op === "not_contains" ||
        filter.op === "!=") &&
      dataType === "string";

    if (dataType === "boolean") {
      return (
        <Select
          fullWidth
          size="small"
          value={filter.value}
          onChange={(e) => handleFilterChange(index, "value", e.target.value)}
          displayEmpty
          onKeyDown={handleKeyDown}
        >
          <MenuItem value="">
            <Typography variant="caption" color={colors.text_secondary}>
              Select Value
            </Typography>
          </MenuItem>
          <MenuItem value="true">
            <Typography variant="caption" color={colors.text_primary}>
              True
            </Typography>
          </MenuItem>
          <MenuItem value="false">
            <Typography variant="caption" color={colors.text_primary}>
              False
            </Typography>
          </MenuItem>
        </Select>
      );
    }

    if (dataType === "number") {
      return (
        <StyledTextField
          fullWidth
          size="small"
          type="number"
          placeholder="Value"
          value={filter.value}
          onChange={(e) => handleFilterChange(index, "value", e.target.value)}
          onKeyDown={handleKeyDown}
          sx={{
            "& .MuiOutlinedInput-input": {
              fontSize: "14px",
              fontFamily: "Poppins, sans-serif",
              lineHeight: "14px",
              letterSpacing: "0.48px",
            },
          }}
        />
      );
    }

    if (isMultiValue) {
      return (
        <StyledTextField
          fullWidth
          size="small"
          multiline
          minRows={1}
          maxRows={4}
          placeholder="Enter values (one per line or comma-separated)"
          value={filter.value}
          onChange={(e) => handleFilterChange(index, "value", e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              minHeight: "39px",
            },
            "& .MuiOutlinedInput-input": {
              fontSize: "15px",
              fontFamily: "Poppins, sans-serif",
              lineHeight: "15px",
              letterSpacing: "0.48px",
            },
          }}
        />
      );
    }

    return (
      <StyledTextField
        fullWidth
        size="small"
        placeholder="Value"
        value={filter.value}
        onChange={(e) => handleFilterChange(index, "value", e.target.value)}
        onKeyDown={handleKeyDown}
        sx={{
          "& .MuiOutlinedInput-input": {
            fontSize: "14px",
            fontFamily: "Poppins, sans-serif",
            lineHeight: "14px",
            letterSpacing: "0.48px",
          },
        }}
      />
    );
  };

  if (isLoadingFields) {
    return (
      <Box>
        <LoadingIcon type="circular" size={16} />
        <Typography variant="caption">Loading filter fields...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 2, mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1">Advanced Search Query</Typography>
        {filters.some((f) => f.field && f.value) && (
          <Typography variant="caption" color={colors.text_secondary}>
            {`${
              filters.filter((f) => f.field && f.value).length
            } active filters`}
          </Typography>
        )}
      </Box>

      {filters.map((filter, index) => (
        <Box key={`filter-${index}-${filter.field}`}>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 1 }}>
            <Grid item xs={4}>
              <Autocomplete
                fullWidth
                size="small"
                options={fieldOptions}
                filterOptions={filterOptions}
                groupBy={(option) => option.group}
                value={
                  fieldOptions.find((opt) => opt.value === filter.field) || null
                }
                onChange={(event, newValue) => {
                  handleFilterChange(index, "field", newValue?.value || "");
                }}
                getOptionLabel={(option) => option.label || ""}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    placeholder="Select Field"
                    sx={{
                      "& .MuiOutlinedInput-input": {
                        fontSize: "14px",
                        fontFamily: "Poppins, sans-serif",
                        lineHeight: "14px",
                        letterSpacing: "0.48px",
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={getFieldKey(option)}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {option.source_integration && (
                        <IntegrationImage
                          name={option.source_integration.toLowerCase()}
                          width={16}
                          newLogo={true}
                          showToolTip={false}
                        />
                      )}
                      <Typography variant="caption" color={colors.text_primary}>
                        {option.formattedName}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <ListSubheader
                      component="div"
                      sx={{
                        backgroundColor: colors.common_white,
                        lineHeight: "32px",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        color={colors.text_secondary}
                      >
                        {params.group}
                      </Typography>
                    </ListSubheader>
                    <ul style={{ padding: 0 }}>{params.children}</ul>
                  </li>
                )}
                ListboxProps={{
                  sx: {
                    maxHeight: "400px",
                    "& .MuiAutocomplete-option": {
                      minHeight: "auto",
                      padding: "8px 16px",
                    },
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: "0px 9px",
                    "& .MuiAutocomplete-input": {
                      padding: "7.5px 4px",
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={3}>
              <Select
                fullWidth
                size="small"
                value={filter.op}
                onChange={(e) =>
                  handleFilterChange(index, "op", e.target.value)
                }
                sx={selectStyles}
              >
                {getFilterOperatorOptions(filter).map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    <Typography variant="caption" color={colors.text_primary}>
                      {op.label}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={4}>
              {renderValueInput(filter, index)}
            </Grid>

            <Grid item xs={1}>
              <IconButton
                onClick={() => removeFilter(index)}
                disabled={
                  filters.length === 1 && index === 0 && !filters[0].field
                }
              >
                <SvgToIcon IconLogo={icons.DeleteIcon} width={18} height={18} />
              </IconButton>
            </Grid>
          </Grid>
          {index < filters.length - 1 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                mb: 1,
                mt: -0.5,
              }}
            >
              <Select
                size="small"
                value={filterLogic[index] || "AND"}
                onChange={(e) => handleLogicChange(index, e.target.value)}
                sx={{
                  minWidth: "80px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "8px",
                    borderColor: colors.divider2,
                  },
                  "& .MuiOutlinedInput-root": {
                    height: "28px",
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "0.5px solid " + colors.divider2,
                    },
                  },
                }}
              >
                <MenuItem value="AND">
                  <Typography variant="caption" color={colors.text_primary}>
                    AND
                  </Typography>
                </MenuItem>
                <MenuItem value="OR">
                  <Typography variant="caption" color={colors.text_primary}>
                    OR
                  </Typography>
                </MenuItem>
              </Select>
            </Box>
          )}
        </Box>
      ))}

      <Box sx={{ mt: 2, display: "flex", gap: 2, alignItems: "center" }}>
        <TextButton
          startIcon={icons.AddIcon}
          onClick={addFilter}
          size="small"
          sx={{
            padding: "4px 8px",
          }}
        >
          Add Filter
        </TextButton>
        <TextButton onClick={handleSearch} size="small">
          Search
        </TextButton>
      </Box>
    </Box>
  );
}
